// LocationTagSelector — combo input + popover, grouped by storage condition.
// Typing a non-matching name surfaces an "Add new location" footer button;
// clicking it switches the popover to an inline configure form where the
// storage condition is set via a nested TypeSelector. Ported from
// tag-selectors.jsx (LocationTagSelector at lines 192-493).

import * as React from "react";
import { createPortal } from "react-dom";
import { RawStorageChip, StorageChip } from "./StorageChip";
import { TypeSelector, type TypeValue } from "./TypeSelector";
import type { TagLocation, TypeCatalogRow } from "./_types";
import { useClickOutside, usePopoverPosition } from "./usePopover";

const CaretDown = ({ size = 11 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Check = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Plus = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const XMark = ({ size = 11 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

type Props = {
  /** Currently-selected location id (single-select per inventory). */
  value: string | null;
  onChange: (locId: string | null) => void;
  locations: TagLocation[];
  storageConditions: TypeCatalogRow[];
  onAddLocation?: (loc: {
    label: string;
    storage_condition_id: string | null;
    storage_condition_raw: string | null;
  }) => void;
};

type AddingState = {
  name: string;
  storage: TypeValue;
} | null;

export function LocationTagSelector({
  value,
  onChange,
  locations,
  storageConditions,
  onAddLocation,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [focused, setFocused] = React.useState(false);
  const [adding, setAdding] = React.useState<AddingState>(null);

  const ref = React.useRef<HTMLDivElement | null>(null);
  const popRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const close = () => {
    setOpen(false);
    setQ("");
    setAdding(null);
  };

  useClickOutside(ref, close, popRef);
  const popPos = usePopoverPosition(triggerRef, open, {
    minWidth: 360,
    align: "start",
    estHeight: 420,
  });

  const sel = locations.find((l) => l.id === value) ?? null;
  const norm = q.trim().toLowerCase();
  const filtered = locations.filter(
    (l) => !norm || l.label.toLowerCase().includes(norm),
  );
  const exact = locations.find((l) => l.label.toLowerCase() === norm);
  const canAdd = norm.length > 0 && !exact;

  const groups = React.useMemo(() => {
    const map = new Map<string, TagLocation[]>();
    for (const l of filtered) {
      const k =
        l.storage_condition_id ||
        (l.storage_condition_raw ? `_raw_${l.storage_condition_raw}` : "_unknown_");
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(l);
    }
    return [...map.entries()];
  }, [filtered]);

  const startAdd = () => {
    setAdding({
      name: q.trim(),
      storage: storageConditions.find((s) => s.id === "ambient")
        ? { id: "ambient" }
        : {},
    });
    setQ("");
  };
  const commitAdd = () => {
    const n = adding?.name?.trim() ?? "";
    if (!n) return;
    onAddLocation?.({
      label: n,
      storage_condition_id: adding?.storage?.id ?? null,
      storage_condition_raw: adding?.storage?.raw ?? null,
    });
    close();
  };

  const showOverlay = !!sel && !open && !focused;
  const inputValue = open ? q : "";
  const inputPlaceholder = sel ? "" : "Search or add a location…";

  const conditionById = React.useMemo(
    () => Object.fromEntries(storageConditions.map((s) => [s.id, s])),
    [storageConditions],
  );

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div
        ref={triggerRef}
        onClick={() => inputRef.current?.focus()}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 10px",
          borderRadius: 5,
          border: `1px solid ${open || focused ? "var(--ink)" : "var(--line-strong)"}`,
          background: "var(--panel)",
          boxShadow: open || focused ? "0 0 0 2px var(--accent-soft)" : "none",
          cursor: "text",
          minHeight: 32,
          transition: "border-color 120ms, box-shadow 120ms",
        }}
      >
        {showOverlay && sel && (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {sel.storage_condition_id ? (
              <StorageChip
                id={sel.storage_condition_id}
                row={conditionById[sel.storage_condition_id]}
                size="sm"
              />
            ) : sel.storage_condition_raw ? (
              <RawStorageChip text={sel.storage_condition_raw} />
            ) : null}
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                color: "var(--ink)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {sel.label}
            </span>
          </span>
        )}
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setQ(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setFocused(true);
            setOpen(true);
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (exact) {
                onChange(exact.id);
                close();
              } else if (canAdd) startAdd();
            }
            if (e.key === "Escape") close();
          }}
          placeholder={inputPlaceholder}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 13,
            color: "var(--ink)",
            position: showOverlay ? "absolute" : "static",
            opacity: showOverlay ? 0 : 1,
            pointerEvents: showOverlay ? "none" : "auto",
            width: showOverlay ? 0 : "auto",
          }}
        />
        <span
          style={{
            color: "var(--ink-3)",
            display: "inline-flex",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 160ms ease",
            flexShrink: 0,
          }}
        >
          <CaretDown />
        </span>
      </div>

      {open && popPos &&
        createPortal(
          <div
            ref={popRef}
            data-popover-portal="1"
            style={{
              position: "fixed",
              left: popPos.left,
              top: popPos.top,
              bottom: popPos.bottom,
              width: popPos.width,
              zIndex: 1000,
              background: "var(--panel)",
              border: "1px solid var(--line-strong)",
              borderRadius: 8,
              boxShadow: "0 12px 32px -8px rgba(20,30,22,0.22)",
              maxHeight: Math.max(280, popPos.maxHeight || 420),
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {adding ? (
              <AddLocationForm
                data={adding}
                onChange={setAdding}
                storageConditions={storageConditions}
                onSave={commitAdd}
                onCancel={() => setAdding(null)}
              />
            ) : (
              <>
                <div style={{ flex: 1, overflow: "auto" }}>
                  {groups.map(([condId, locs]) => {
                    const isRaw = condId.startsWith("_raw_");
                    const rawText = isRaw ? condId.slice(5) : null;
                    const isUnknown = condId === "_unknown_";
                    return (
                      <div key={condId}>
                        <div
                          style={{
                            padding: "7px 12px",
                            background: "var(--panel-2)",
                            borderBottom: "1px solid var(--line-2)",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {isRaw && rawText ? (
                            <RawStorageChip text={rawText} />
                          ) : isUnknown ? (
                            <span
                              className="mono"
                              style={{
                                fontSize: 9,
                                color: "var(--ink-4)",
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                fontWeight: 700,
                              }}
                            >
                              No condition set
                            </span>
                          ) : (
                            <StorageChip
                              id={condId}
                              row={conditionById[condId]}
                              size="sm"
                              showHint
                            />
                          )}
                        </div>
                        {locs.map((l) => {
                          const isSel = value === l.id;
                          return (
                            <button
                              key={l.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                onChange(l.id);
                                close();
                              }}
                              style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "8px 14px 8px 22px",
                                border: "none",
                                textAlign: "left",
                                cursor: "pointer",
                                background: isSel
                                  ? "var(--accent-soft)"
                                  : "transparent",
                                borderLeft: `3px solid ${isSel ? "var(--accent)" : "transparent"}`,
                              }}
                              onMouseEnter={(e) => {
                                if (!isSel)
                                  e.currentTarget.style.background =
                                    "var(--bg-sunk)";
                              }}
                              onMouseLeave={(e) => {
                                if (!isSel)
                                  e.currentTarget.style.background =
                                    "transparent";
                              }}
                            >
                              <span
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  fontSize: 12.5,
                                  color: "var(--ink-2)",
                                  fontWeight: 500,
                                }}
                              >
                                {l.label}
                              </span>
                              {isSel && (
                                <span style={{ color: "var(--accent-ink)" }}>
                                  <Check />
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                  {filtered.length === 0 && !canAdd && (
                    <div
                      style={{
                        padding: "14px 14px",
                        color: "var(--ink-4)",
                        fontSize: 12,
                        fontStyle: "italic",
                        textAlign: "center",
                      }}
                    >
                      No locations yet.
                    </div>
                  )}
                </div>

                {canAdd && onAddLocation && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={startAdd}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: "var(--accent-soft)",
                      color: "var(--accent-ink)",
                      border: "none",
                      borderTop: "1px solid var(--accent-line)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      textAlign: "left",
                    }}
                  >
                    <Plus size={12} />
                    <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                      Add new location:{" "}
                      <strong style={{ fontStyle: "italic" }}>
                        "{q.trim()}"
                      </strong>
                    </span>
                  </button>
                )}
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

function AddLocationForm({
  data,
  onChange,
  storageConditions,
  onSave,
  onCancel,
}: {
  data: NonNullable<AddingState>;
  onChange: (next: NonNullable<AddingState>) => void;
  storageConditions: TypeCatalogRow[];
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      style={{
        padding: 14,
        background: "var(--panel-2)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        className="hstack"
        style={{ gap: 8, justifyContent: "space-between" }}
      >
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: "var(--ink)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          Configure new location
        </span>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel add location"
          style={{
            display: "inline-flex",
            padding: 3,
            border: "none",
            background: "transparent",
            color: "var(--ink-4)",
            cursor: "pointer",
            borderRadius: 3,
          }}
        >
          <XMark size={11} />
        </button>
      </div>

      <div className="vstack" style={{ gap: 5 }}>
        <span
          className="mono"
          style={{
            fontSize: 9,
            color: "var(--ink-4)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontWeight: 600,
          }}
        >
          Name
        </span>
        <input
          autoFocus
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          placeholder="e.g. Shelf D-3"
          style={{
            width: "100%",
            padding: "7px 10px",
            fontSize: 12.5,
            border: "1px solid var(--line)",
            borderRadius: 4,
            background: "var(--panel)",
            outline: "none",
          }}
        />
      </div>

      <div className="vstack" style={{ gap: 5 }}>
        <span
          className="mono"
          style={{
            fontSize: 9,
            color: "var(--ink-4)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontWeight: 600,
          }}
        >
          Storage condition · click chip to change
        </span>
        <TypeSelector
          value={data.storage}
          onChange={(v) => onChange({ ...data, storage: v })}
          catalog={storageConditions}
          rawEntries={[]}
          placeholder="Pick a condition…"
          renderLabel={(r) => (
            <>
              <StorageChip id={r.id} row={r} size="sm" />
              <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                {r.hint}
              </span>
            </>
          )}
          renderTrigger={(row) =>
            row ? <StorageChip id={row.id} row={row} size="lg" showHint /> : null
          }
        />
      </div>

      <div className="hstack" style={{ justifyContent: "flex-end", gap: 6 }}>
        <button className="btn xs" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn xs primary"
          disabled={!data.name.trim()}
          style={{ opacity: data.name.trim() ? 1 : 0.4 }}
          onClick={onSave}
        >
          <Check size={11} />
          Save location
        </button>
      </div>
    </div>
  );
}
